/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { aiModelsService } from '../../services/ai-models.service';
import { evalRunService } from '../../services/eval-run.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { TriggeredEvalRun } from '../../types';
import EvalRunResult from './eval-run-result';

interface EvalRunTriggerPanelProps {
  suiteRef: string;
}

/** Server-side cap on the run's purpose — EvalRunRequestSchema.purpose. */
const PURPOSE_MAX = 500;

/**
 * Trigger-a-suite-run form + live results. Meant to be rendered inside a Dialog.
 * On submit it triggers the run then live-polls the returned run to completion.
 */
const EvalRunTriggerPanel = ({ suiteRef }: EvalRunTriggerPanelProps) => {
  const [purpose, setPurpose] = useState('');
  const [modelId, setModelId] = useState<string | null>(null);
  const [concurrency, setConcurrency] = useState<number | null>(null);
  const [trials, setTrials] = useState<number | null>(null);

  const { data: aiModelsResp } = useQuery({
    // runnable_only: an embedding model cannot be sent a prompt, so offering
    // one here can only produce a run that fails at the provider.
    queryKey: ['ai-models', 'runnable'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100, runnable_only: true }),
    staleTime: 5 * 60 * 1000,
  });
  // Send the unique _id, not modelId: several rows can share a modelId
  // (e.g. gpt-oss-120b registered under multiple providers), and resolving by
  // name then fails with AI_MODEL_AMBIGUOUS.
  const modelOptions = (aiModelsResp?.data || []).map((m) => ({
    value: m._id,
    label: m.displayName ? `${m.displayName} (${m.modelId})` : m.modelId,
  }));

  const mutation = useMutation<TriggeredEvalRun, any, void>({
    mutationFn: () =>
      evalRunService.trigger(suiteRef, {
        purpose: purpose.trim() || undefined,
        modelId: modelId || undefined,
        concurrency: concurrency ?? undefined,
        trials: trials && trials > 1 ? trials : undefined,
      }),
  });

  const triggered = mutation.data;
  const runRef = triggered?.evalRunId || triggered?._id;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Purpose</label>
          {/* Capped where the server caps it (EvalRunRequestSchema.purpose,
              max_length 500). Without this the only feedback on a long paste was
              a pydantic validation error after the run was submitted. */}
          <InputText
            value={purpose}
            onChange={(e) => setPurpose(e.target.value.slice(0, PURPOSE_MAX))}
            maxLength={PURPOSE_MAX}
            placeholder="Optional — why this run"
            className="w-full"
          />
          {purpose.length > PURPOSE_MAX - 50 && (
            <span className="text-xs text-ink-muted">{purpose.length} / {PURPOSE_MAX}</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Model override
            </label>
            <Dropdown
              value={modelId}
              options={modelOptions}
              onChange={(e: any) => setModelId(e.value || null)}
              placeholder="Use eval default"
              filter
              showClear
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Concurrency (1–16)
            </label>
            <InputNumber
              value={concurrency}
              onValueChange={(e) => setConcurrency(e.value ?? null)}
              min={1}
              max={16}
              useGrouping={false}
              placeholder="Default"
              className="w-full"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="eval-run-trials" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Trials per case (1–10)
            </label>
            {/* One answer is one sample. Repeating each case shows whether it
                passes reliably (pass^k) or only sometimes — at k× the cost. */}
            <InputNumber
              inputId="eval-run-trials"
              value={trials}
              onValueChange={(e) => setTrials(e.value ?? null)}
              min={1}
              max={10}
              useGrouping={false}
              placeholder="1"
              className="w-full"
            />
            {trials != null && trials > 1 && (
              <span className="text-xs text-ink-muted">
                A case passes only if all {trials} trials pass. Costs about {trials}× a single run.
              </span>
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            label={mutation.isPending ? 'Starting…' : 'Run'}
            icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            size="small"
            data-testid="EvalRunTrigger.Button.Run"
            className="ui-press"
          />
        </div>
      </div>

      {mutation.isError && (
        <Message
          severity="error"
          text={extractErrorMessage(mutation.error, 'Failed to trigger eval run')}
          className="w-full"
        />
      )}

      {mutation.isPending && (
        <div className="ui-rise flex items-center gap-2 p-3 text-sm text-ink-muted">
          <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="6" />
          Starting run…
        </div>
      )}

      {runRef && <EvalRunResult runRef={runRef} title="Eval Run" />}
    </div>
  );
};

export default EvalRunTriggerPanel;
